'use strict';

(() => {
  const requests = new WeakMap();
  const regionNames = ['featured', 'price', 'gallery', 'purchase'];

  function selectionValues(section) {
    return Array.from(section.querySelectorAll('[data-option-value]'), (input) => input.value);
  }

  function showStatus(section, message, reloadUrl) {
    const status = section.querySelector('[data-product-status]');
    status.hidden = false;
    status.replaceChildren(document.createTextNode(message));
    if (reloadUrl) {
      const link = document.createElement('a');
      link.className = 'text-link';
      link.href = reloadUrl;
      link.textContent = 'Reload this selection';
      status.append(document.createTextNode(' '), link);
    }
  }

  function blockPurchase(section) {
    const purchase = section.querySelector('[data-product-purchase]');
    purchase.setAttribute('aria-busy', 'true');
    purchase.querySelectorAll('button[type="submit"], input[name="id"], input[name="quantity"]')
      .forEach((input) => { input.disabled = true; });
  }

  async function updateSelection(section) {
    const previous = requests.get(section);
    if (previous) previous.controller.abort();
    const state = { controller: new AbortController(), blocked: true };
    requests.set(section, state);

    const selected = selectionValues(section);
    const quantity = section.querySelector('input[name="quantity"]').value;
    const selectionUrl = new URL(window.location.href);
    selectionUrl.searchParams.delete('variant');
    selectionUrl.searchParams.delete('sections');
    selectionUrl.searchParams.delete('section_id');
    selectionUrl.searchParams.set('option_values', selected.join(','));
    const requestUrl = new URL(selectionUrl);
    requestUrl.searchParams.set('section_id', section.dataset.productSection);

    blockPurchase(section);
    showStatus(section, 'Updating your selection…');
    const timeout = window.setTimeout(() => state.controller.abort(), 15000);

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: state.controller.signal,
      });
      if (!response.ok) throw new Error('Product details could not be loaded.');
      const html = await response.text();
      if (requests.get(section) !== state || !section.isConnected) return;

      const page = new DOMParser().parseFromString(html, 'text/html');
      const rendered = Array.from(page.querySelectorAll('[data-product-section]'))
        .find((candidate) => candidate.dataset.productSection === section.dataset.productSection);
      if (!rendered || selectionValues(rendered).join(',') !== selected.join(',')) {
        throw new Error('The product selection was not returned.');
      }
      const regions = regionNames.map((name) => ({
        current: section.querySelector(`[data-product-${name}]`),
        next: rendered.querySelector(`[data-product-${name}]`),
      }));
      if (regions.some((region) => !region.current || !region.next)) {
        throw new Error('Product details were incomplete.');
      }
      const nextQuantity = rendered.querySelector('input[name="quantity"]');
      const nextVariant = rendered.querySelector('input[name="id"]');
      const nextSubmit = rendered.querySelector('[data-product-purchase] button[type="submit"]');
      if (!nextQuantity || !nextVariant || !nextSubmit || (!nextVariant.value && !nextSubmit.disabled)) {
        throw new Error('The product form was incomplete.');
      }
      const defaultQuantity = nextQuantity.value;
      nextQuantity.value = quantity;
      if (!nextQuantity.checkValidity()) nextQuantity.value = defaultQuantity;
      // Keep the live picker and app blocks mounted, preserving keyboard focus.
      regions.forEach(({ current, next }) => current.replaceChildren(...next.childNodes));
      section.querySelector('[data-product-purchase]').removeAttribute('aria-busy');
      window.history.replaceState(window.history.state, '', selectionUrl);
      state.blocked = false;
      const price = section.querySelector('[data-product-price]').textContent.trim();
      const availability = nextSubmit.disabled ? 'This selection is unavailable.' : 'Ready to add to your bag.';
      showStatus(section, `Selection updated. ${price}. ${availability}`);
    } catch (error) {
      if (requests.get(section) !== state || !section.isConnected) return;
      // Keep the old variant disabled until a matching response or full reload succeeds.
      blockPurchase(section);
      section.querySelector('[data-product-purchase]').removeAttribute('aria-busy');
      showStatus(section, 'We could not update this selection. Reload it before adding to your bag.', selectionUrl);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-option-value]')) return;
    const section = event.target.closest('[data-product-section]');
    if (section) updateSelection(section);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-product-purchase]')) return;
    const section = form.closest('[data-product-section]');
    const variant = form.querySelector('input[name="id"]');
    const button = form.querySelector('button[type="submit"]');
    if (requests.get(section)?.blocked || !variant?.value || variant.disabled || button?.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
})();
